/* ═══════════════════════════════════════════════════════════════════════════
   Le pont simulation → présentation.

   C'est le SEUL canal par lequel la sim parle au rendu, à l'audio et au HUD.
   La règle de dépendance (PLAN.md §3.1) est à sens unique :

       platform  ←  sim  ←  content  ←  view / audio / ui

   La sim ne connaît ni three, ni le DOM, ni l'audio. Quand un ennemi meurt,
   elle n'allume pas une explosion : elle pousse un événement. Le rendu et
   l'audio le draînent une fois par frame et décident quoi en faire.

   Ce que ça achète concrètement : la sim reste rejouable dans Node sans GPU
   (tests headless, re-simulation serveur), et couper un effet visuel ne peut
   pas modifier le déroulement d'une partie.

   Stockage : un seul Float32Array préalloué, à pas de 5. Les entiers (type,
   identifiants) y sont exacts jusqu'à 2^24 — largement au-delà de ce qu'on
   compte ici — ce qui évite un point fixe et une seconde structure.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Ev = {
  EnemyDamaged: 0,
  EnemyKilled: 1,
  PlayerHit: 2,
  PlayerDied: 3,
  PlayerRespawned: 4,
  BulletBlocked: 5,
  Graze: 6,
  PickupCollected: 7,
  WaveStarted: 8,
  BombUsed: 9,
  /** Le joueur a annulé sa mort dans la fenêtre de death-bomb. Émis pour que la
   *  présentation puisse le souligner : c'est le moment le plus gratifiant du
   *  jeu, et il doit s'entendre. */
  DeathBombed: 10,
  /** Arme secondaire déclenchée. Émis AU DÉCLENCHEMENT seulement, jamais à
   *  chaque volée d'une rafale : la présentation doit souligner la décision du
   *  joueur, pas chacune de ses conséquences. `a` = index de l'arme,
   *  `b`/`c` = position, `d` = forme de tir. */
  SecondaryFired: 11,
  /** Un ennemi MENAÇANT a franchi le bas de l'écran sans être abattu. Émis pour
   *  que la présentation puisse le souligner : laisser fuir a un coût, et le
   *  joueur doit le voir. `a` = valeur perdue, `b`/`c` = position. */
  EnemyEscaped: 12,
  /** Un obstacle destructible est tombé à zéro PV et se brise. Émis pour que la
   *  vue déclenche l'éclatement (débris) et l'audio le fracas : un astéroïde qui
   *  disparaît sans bruit ni gerbe se lit comme un bug d'affichage, pas comme une
   *  destruction. `a` = type d'obstacle, `b`/`c` = position, `d` = rayon (la vue
   *  dimensionne l'explosion dessus). Un obstacle INDESTRUCTIBLE n'émet jamais
   *  cet événement : il ne se brise pas. */
  ObstacleBroken: 13,
  /** Le BOUCLIER du joueur a encaissé un coup à sa place. Émis pour que l'audio
   *  joue le tintement d'absorption : sans lui, le joueur ne sait pas que le
   *  bouclier vient de le sauver, et la ressource paraît décorative. Distinct de
   *  `PlayerHit` (le bouclier a justement ÉVITÉ le dégât). `b`/`c` = position,
   *  `d` = charge de bouclier restante. Les événements ne sont pas hachés (ils
   *  ne pilotent aucun tick futur), donc ce nouveau type est sans effet sur le
   *  golden. */
  ShieldBlocked: 14,
  /** Un projectile joueur a FRAPPÉ un obstacle sans le briser (il l'entame, ou
   *  rebondit sur un obstacle indestructible). Émis pour l'audio d'IMPACT — le
   *  « tok » du tir qui mord la roche, distinct du fracas de la casse
   *  (`ObstacleBroken`). `b`/`c` = point d'impact, `d` = dégâts infligés (0 si
   *  indestructible). Événement non haché → sans effet sur le golden. */
  ObstacleHit: 15,
  /** Un ennemi TIRE (un vrai tir, pas un tir scellé/télégraphié annulé). Émis
   *  pour que l'audio donne une voix aux menaces — surtout les patterns spéciaux
   *  (laser, spirale, salve). `a` = le PATTERN (valeur de `Pattern`), `b`/`c` =
   *  position. Fréquent en bullet-hell : l'audio en fait une texture throttlée
   *  par le pool de voix, et ne sort du lot que les patterns marquants.
   *  Événement non haché → sans effet sur le golden. */
  EnemyFired: 16,
  /** L'ULTI est déclenchée : début de la CHARGE (le télégraphe). `b`/`c` =
   *  position du joueur. Pour la présentation : le halo/trou-noir qui se charge
   *  et le son de montée en puissance. */
  UltiCharge: 17,
  /** La CHARGE aboutit : DÉCHARGE. `a` = index de la secondaire déchargée,
   *  `b`/`c` = position. Pour la présentation : le flash, l'onde, le faisceau
   *  énorme, et le son de déflagration. Émis UNE fois, au début de la rafale. */
  UltiDischarge: 18,
  /** Un ennemi KAMIKAZE déclenche son PIQUÉ (fin du télégraphe de pause avant de
   *  plonger, Motion.Dive). Émis UNE fois à la bascule. `b`/`c` = position. Pour
   *  l'audio : le rugissement du plongeon. Non haché → sans effet golden. */
  EnemyDive: 19,
  /** Le canon vient d'entrer en SURCHAUFFE (front). Émis une fois pour le son de
   *  chauffe/alerte. `b`/`c` = position du joueur. Non haché → sans effet golden. */
  Overheat: 20,
  /** Un BOSS vient d'APPARAÎTRE. Émis une fois, au spawn du boss (`levelSystem`).
   *  Pour la mise en scène : stinger d'entrée, bascule d'ambiance musicale, barre
   *  de boss. `b`/`c` = position. Non haché → sans effet golden. */
  BossSpawned: 21,
  /** Un boss change de PHASE de combat (franchit un seuil de PV décroissant). Émis
   *  une fois par transition (`phaseSystem`). `a` = index de la NOUVELLE phase,
   *  `b`/`c` = position. Pour la présentation : flash, son de bascule, montée
   *  d'intensité. Non haché → sans effet golden. */
  BossPhase: 22,
  /** Un BOSS vient d'être ABATTU (par opposition à un ennemi ordinaire). Émis à sa
   *  mort (`damageSystem`), pour une fanfare dédiée et non l'explosion générique.
   *  `b`/`c` = position. Non haché → sans effet golden. */
  BossKilled: 23,
  /** Le canon vient de s'ENRAYER (front) : tir bloqué net pour avoir trop tiré en
   *  surchauffe. Émis une fois pour le clac/sifflement d'enrayage. `b`/`c` =
   *  position du joueur. Non haché → sans effet golden. */
  Jam: 24,
} as const;

export type EvType = (typeof Ev)[keyof typeof Ev];

const STRIDE = 5;

export class EventRing {
  private readonly buf: Float32Array;
  private readonly cap: number;
  private n = 0;
  /** Curseur de lecture. Les événements s'accumulent sur TOUS les sous-pas
   *  d'une frame et ne sont vidés qu'une fois le rendu fait ; un consommateur
   *  qui veut réagir dès le tick — le hitstop, par exemple, dont 8 ms de retard
   *  se sentent — lit à partir d'ici plutôt que de tout reparcourir et de tout
   *  compter deux fois. */
  private read = 0;
  /** Événements perdus par saturation depuis le dernier drain. Non nul = on a
   *  sous-dimensionné, ou une boucle émet en rafale. À surveiller en dev. */
  dropped = 0;

  constructor(capacity = 512) {
    this.cap = capacity;
    this.buf = new Float32Array(capacity * STRIDE);
  }

  get count(): number {
    return this.n;
  }

  /** a/b/c/d sont libres d'interprétation selon le type. Convention retenue :
   *  a = identifiant concerné, b/c = position x/y, d = magnitude. */
  push(type: EvType, a: number, b: number, c: number, d: number): void {
    if (this.n >= this.cap) {
      this.dropped++;
      return;
    }
    const o = this.n * STRIDE;
    this.buf[o] = type;
    this.buf[o + 1] = a;
    this.buf[o + 2] = b;
    this.buf[o + 3] = c;
    this.buf[o + 4] = d;
    this.n++;
  }

  type(i: number): number {
    return this.buf[i * STRIDE]!;
  }
  a(i: number): number {
    return this.buf[i * STRIDE + 1]!;
  }
  b(i: number): number {
    return this.buf[i * STRIDE + 2]!;
  }
  c(i: number): number {
    return this.buf[i * STRIDE + 3]!;
  }
  d(i: number): number {
    return this.buf[i * STRIDE + 4]!;
  }

  /** Index du premier événement non encore lu. */
  get readIndex(): number {
    return this.read;
  }
  /** Nombre d'événements arrivés depuis le dernier `markRead()`. */
  get unread(): number {
    return this.n - this.read;
  }
  /** Déclare tout ce qui précède comme consommé. */
  markRead(): void {
    this.read = this.n;
  }

  /** Appelé une fois par frame de rendu, APRÈS que le rendu et l'audio ont lu.
   *  Ne libère rien : remet juste les curseurs à zéro. */
  clear(): void {
    this.n = 0;
    this.read = 0;
    this.dropped = 0;
  }
}
